import { isBefore } from 'date-fns';
import type { Payment } from '@erp/rpc';
import {
  absMoney,
  compareMoney,
  parseMoney,
  subtractMoney,
  type Money,
} from '@erp/shared-utils';
import { parseDateOnly } from '@/utils/dateUtils';

type PaymentInvoice = NonNullable<Payment['invoices']>[number];

type AllocationView =
  | { kind: 'unknown'; allocated: string }
  | {
      kind: 'known';
      allocated: string;
      invoiceTotal: string;
      priorPaid: string;
      remainingBefore: string;
      remainingAfter: string;
      tone: 'cleared' | 'remaining' | 'excess';
    };

function allocationTone(
  remainingAfter: Money,
): 'cleared' | 'remaining' | 'excess' {
  if (compareMoney(absMoney(remainingAfter), '0.005') < 0) return 'cleared';
  return compareMoney(remainingAfter, 0) > 0 ? 'remaining' : 'excess';
}

export function getInvoiceAllocationView({
  invoice,
  paidAmountIncludesAllocation,
}: {
  invoice: PaymentInvoice;
  paidAmountIncludesAllocation: boolean;
}): AllocationView {
  const allocated = parseMoney(invoice.amount);
  if (invoice.totalAmount === undefined) {
    return { kind: 'unknown', allocated: allocated.toString() };
  }
  const invoiceTotal = parseMoney(invoice.totalAmount);
  const rawPaidAmount = parseMoney(invoice.paidAmount ?? 0);
  const priorPaid = paidAmountIncludesAllocation
    ? subtractMoney(rawPaidAmount, allocated)
    : rawPaidAmount;
  const remainingBefore = subtractMoney(invoiceTotal, priorPaid);
  const remainingAfter = subtractMoney(remainingBefore, allocated);
  return {
    kind: 'known',
    allocated: allocated.toString(),
    invoiceTotal: invoiceTotal.toString(),
    priorPaid: priorPaid.toString(),
    remainingBefore: remainingBefore.toString(),
    remainingAfter: remainingAfter.toString(),
    tone: allocationTone(remainingAfter),
  };
}

type DueView =
  | { kind: 'missing' }
  | {
      kind: 'known';
      date: Date;
      status: 'overdue' | 'before-payment' | 'on-time';
    };

function dueStatus({
  dueDate,
  paymentStatus,
  paymentDate,
  todayStart,
}: {
  dueDate: Date;
  paymentStatus: PaymentInvoice['paymentStatus'];
  paymentDate: Date | null;
  todayStart: Date;
}): 'overdue' | 'before-payment' | 'on-time' {
  if (paymentStatus !== 'paid' && isBefore(dueDate, todayStart))
    return 'overdue';
  if (paymentDate !== null && isBefore(dueDate, paymentDate))
    return 'before-payment';
  return 'on-time';
}

export function getInvoiceDueView({
  invoice,
  paymentDate,
  todayStart,
}: {
  invoice: PaymentInvoice;
  paymentDate: Date | null;
  todayStart: Date;
}): DueView {
  const dueDate = invoice.dueDate ? parseDateOnly(invoice.dueDate) : null;
  if (dueDate === null) return { kind: 'missing' };
  return {
    kind: 'known',
    date: dueDate,
    status: dueStatus({
      dueDate,
      paymentStatus: invoice.paymentStatus,
      paymentDate,
      todayStart,
    }),
  };
}
